import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TOKEN_KEY = 'customerToken';

// Customer-side auth context. Stores the JWT in localStorage under a key
// distinct from the admin token (`adminToken` is used by the admin shell)
// so an admin and a customer can be logged in in different tabs without
// stomping each other.
//
// `customer` shape: { id, email, name, email_verified } — minimal payload
// from /login or /reset-password. Refreshed via /me on mount when a token
// is in storage.

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const persistToken = useCallback((value) => {
    setToken(value);
    try {
      if (value) localStorage.setItem(TOKEN_KEY, value);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {}
  }, []);

  const logout = useCallback(() => {
    persistToken(null);
    setCustomer(null);
  }, [persistToken]);

  const refresh = useCallback(async (overrideToken) => {
    const t = overrideToken ?? token;
    if (!t) {
      setCustomer(null);
      setLoading(false);
      return null;
    }
    try {
      const response = await fetch(`${API_URL}/api/customers/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (response.status === 401 || response.status === 403) {
        // Token expired / invalid — clear and redirect-on-next-render
        // through RequireCustomerAuth.
        persistToken(null);
        setCustomer(null);
        setLoading(false);
        return null;
      }
      const data = await response.json();
      if (data.success) {
        setCustomer(data.customer);
        return data.customer;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('CustomerAuth refresh:', err);
    } finally {
      setLoading(false);
    }
    return null;
  }, [token, persistToken]);

  // Initial load: if a token is already in storage, fetch /me to populate
  // the customer object before the dashboard renders.
  useEffect(() => {
    if (token) refresh();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await fetch(`${API_URL}/api/customers/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json();
    if (data.success && data.token) {
      persistToken(data.token);
      setCustomer(data.customer);
      return { success: true, customer: data.customer };
    }
    return { success: false, message: data.message || 'Login failed' };
  }, [persistToken]);

  // Helper used by pages that just received a token from a different
  // endpoint (e.g. ResetPasswordPage gets a token from /reset-password,
  // or Step4Submit's inline sign-in dialog calls login + here).
  //
  // Note the explicit `overrideToken` plumbing into refresh: React's
  // setState (`persistToken` calls setToken under the hood) is async, so
  // when refresh's useCallback closure reads `token` it'd see the OLD
  // value. Passing the new token through as an argument bypasses that
  // staleness — the same reason the same pattern shows up wherever a
  // freshly-set state needs to feed an immediately-following effect.
  const acceptToken = useCallback(async (newToken, customerStub) => {
    persistToken(newToken);
    if (customerStub) setCustomer(customerStub);
    else await refresh(newToken);
  }, [persistToken, refresh]);

  return (
    <CustomerAuthContext.Provider
      value={{ customer, token, loading, login, logout, refresh, acceptToken }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) {
    throw new Error('useCustomerAuth must be used inside <CustomerAuthProvider>');
  }
  return ctx;
}

// Helper for pages that authenticated-fetch with the customer token.
// Auto-logout on 401 (token invalid/expired) — keeps stale-token UX
// clean. We deliberately DO NOT log out on 403, which means "your token
// is valid but you're not authorized for this resource." A 403 should
// surface the forbidden state in the page, not kick the user back to
// login. Today no customer endpoint returns 403 to a customer JWT, but
// future per-resource gates (e.g. email-verified-required routes) will.
export function useCustomerFetch() {
  const { token, logout } = useCustomerAuth();
  return useCallback(async (path, options = {}) => {
    const url = path.startsWith('http') ? path : `${API_URL}${path}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
    if (response.status === 401) {
      logout();
    }
    return response;
  }, [token, logout]);
}
